import {
  ByFileModuleDTO,
  Module,
  ModuleList,
  ModuleListBase,
} from 'root/apps/ModuleCompareUI/ModuleCompare.types';

export const mergeModuleLists = (
  currentList: ModuleList,
  importedList: ModuleListBase,
  currentAccountId: string,
  importedAccountId: string
): ModuleList => {
  // Create a map of imported modules keyed by their names for easy lookup
  const importedMap = Object.values(importedList).reduce((map, module) => {
    map[module.name] = module; // Key modules by name
    return map;
  }, {} as Record<Module['name'], Module>);

  // Merge the current list with the imported list
  const mergedCurrentList = Object.keys(currentList).reduce((result, key) => {
    const module = currentList[key];
    const existsInImported = !!importedMap[module.name]; // Check if the module exists in the imported list
    result[key] = {
      ...module,
      [importedAccountId]: existsInImported, // Dynamically assign account ID as key
    };
    return result;
  }, {} as ModuleList);

  // Add items from the imported list that are not in the current list
  const additionalModules = Object.keys(importedList).reduce((result, key) => {
    const module = importedList[key];
    if (!Object.values(currentList).some((item) => item.name === module.name)) {
      result[key] = {
        ...module,
        [currentAccountId]: false,
        [importedAccountId]: true,
        canEdit: true,
      } as ModuleList[string];
    }
    return result;
  }, {} as ModuleList);

  // Combine the two results
  return { ...mergedCurrentList, ...additionalModules };
};

export const parseModuleCSV = (csvDataRaw: string): Omit<ByFileModuleDTO, 'fileName'> => {
  if (!csvDataRaw || typeof csvDataRaw !== 'string') {
    throw new Error('Invalid CSV data');
  }

  const rows = csvDataRaw.trim().split('\n');
  const headers = rows[0]?.split(',').map((header) => header.replace(/"/g, '').trim()) || [];
  const dataRows = rows.slice(1);

  const result: ModuleListBase = dataRows.reduce((acc, row) => {
    if (!row) return acc;

    const values = row.split(',').map((value) => value.replace(/"/g, '').trim());
    const idIndex = headers.indexOf('ID');
    const nameIndex = headers.indexOf('Name');
    const descriptionIndex = headers.indexOf('Description');

    if (idIndex === -1 || nameIndex === -1 || descriptionIndex === -1) {
      throw new Error('Invalid CSV structure');
    }

    const id = values[idIndex] || null;
    const name = values[nameIndex] || null;
    const description = values[descriptionIndex] || null;

    if (id && name) {
      acc[id] = { name, description: description || '', owner: '' };
    }

    return acc;
  }, {} as ModuleListBase);

  return {
    moduleList: result,
    accountID: null,
    accountName: null,
    environment: null,
  };
};

export const parseModuleJSON = (jsonDataRaw: string): Omit<ByFileModuleDTO, 'fileName'> => {
  // JSON parsing logic
  const jsonData = JSON.parse(jsonDataRaw);
  if (!jsonData?.data?.moduleList || typeof jsonData.data.moduleList !== 'object') {
    throw new Error('Invalid JSON structure');
  }

  const moduleList = jsonData.data.moduleList;
  const accountID = jsonData.data.accountID;
  const accountName = jsonData.data.accountName;
  const environment = jsonData.data.environment;

  // Transform the module list
  const transformedModuleList: ModuleListBase = Object.keys(moduleList).reduce((acc, key) => {
    const module = moduleList[key];
    if (module?.name && typeof module.name === 'string') {
      acc[key] = {
        name: module.name,
        description: module.description || '',
        owner: module.owner || '',
      };
    }
    return acc;
  }, {} as ModuleListBase);

  // Construct the transformedData object
  return {
    moduleList: transformedModuleList,
    accountID,
    accountName,
    environment,
  };
};
